import {
  Alert,
  Badge,
  BrandMark,
  type BrandName,
  Button,
  Card,
  Chip,
  ChoiceChips,
  Facts,
  Icon,
  type IconName,
  ListRow,
  Row,
  SkeletonRows,
  Stack,
  Status,
  Text,
  useConfirm,
  useTheme,
} from "@rally/ui-kit";
import { useCallback, useEffect, useState } from "react";
import { say } from "../localization/say.ts";
import { providerName, scopeWord, statusWord } from "./integration-said.ts";

const sayPanel = (label: string) => say("integrations.panel")(label);

import type { Card as Panel } from "../chat/panel/stacking.ts";
import { act, read } from "../net/rally.ts";

export type Held = {
  id: string;
  account: string;
  provider: string;
  label: string;
  scopes: string[];
  status: string;
  error: string | null;
  connectedAt: string;
  connectedBy: string;
};

type HeldGrant = {
  id: string;
  agent: string;
  scopes: string[];
  grantedAt: string;
  revokedAt: string | null;
};

type Agent = { account: string; handle: string; owner: string };

type HeldEvent = {
  id: string;
  kind: string;
  at: string;
  by: string;
  act: string | null;
  scope: string | null;
  items: number | null;
};

export function providerIcon(provider: string): IconName {
  if (provider === "google") return "mail";
  if (provider === "proton") return "shield";
  if (provider === "whatsapp") return "message";
  return "link";
}

export function providerMark(provider: string): BrandName | null {
  if (provider === "google") return "google";
  if (provider === "proton") return "proton";
  if (provider === "whatsapp") return "whatsapp";
  if (provider === "telegram") return "telegram";
  if (provider === "slack") return "slack";
  if (provider === "youtube") return "youtube";
  return null;
}

export function ServiceMark({ provider, size = 22 }: { provider: string; size?: number }) {
  const mark = providerMark(provider);
  return mark ? (
    <BrandMark name={mark} size={size} tone="brand" />
  ) : (
    <Icon name={providerIcon(provider)} size={size} color="text.secondary" />
  );
}

export function statusToneOf(status: string): "success" | "danger" | "neutral" {
  return statusTone(status);
}

function statusTone(status: string): "success" | "danger" | "neutral" {
  if (status === "connected") return "success";
  if (status === "error") return "danger";
  return "neutral";
}

function eventLine(held: HeldEvent): string {
  const when = new Date(held.at).toLocaleString();
  if ((held.kind === "integration.called" || held.kind === "connector.called") && held.act && held.scope) {
    return say("integrations.activity.row")(held.act, scopeWord(held.scope), held.items ?? 0);
  }
  if (held.kind === "integration.connected" || held.kind === "connector.connected")
    return say("integrations.connectedWhen")(when);
  if (held.kind === "integration.granted" || held.kind === "connector.granted")
    return say("integrations.granted")(when);
  if (held.kind === "integration.revoked" || held.kind === "connector.revoked")
    return say("integrations.event.revoked")(when);
  if (held.kind === "integration.disconnected" || held.kind === "connector.disconnected")
    return say("integrations.event.disconnected")(when);
  return held.kind;
}

export function integrationPanel(integration: Held, me: string): Panel {
  return {
    key: `integration:${integration.id}`,
    title: sayPanel(integration.label),
    render: () => <IntegrationDetails integration={integration} me={me} />,
  };
}

function IntegrationDetails({ integration: held, me }: { integration: Held; me: string }) {
  const theme = useTheme();
  const [grants, setGrants] = useState<HeldGrant[] | null>(null);
  const [events, setEvents] = useState<HeldEvent[] | null>(null);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [picking, setPicking] = useState(false);
  const [pickedAgent, setPickedAgent] = useState<string | null>(null);
  const [pickedScopes, setPickedScopes] = useState<string[]>([]);
  const [saying, setSaying] = useState<string | null>(null);
  const { ask, dialog } = useConfirm();
  const [test, setTest] = useState<{ ok: boolean; said: string } | null>(null);

  const again = useCallback(() => {
    void read<HeldGrant[]>("integration.grants", { integration: held.id })
      .then(setGrants)
      .catch(() => setGrants([]));
  }, [held.id]);

  const readActivity = useCallback(() => {
    void read<HeldEvent[]>("integration.activity", { integration: held.id })
      .then(setEvents)
      .catch(() => setEvents([]));
  }, [held.id]);

  useEffect(() => {
    void read<Agent[]>("my.agents")
      .then(setAgents)
      .catch(() => setAgents([]));
    again();
    readActivity();
  }, [again, readActivity]);

  const mine = held.connectedBy === me;
  const live = held.scopes ?? [];
  const open = (grants ?? []).filter((one) => !one.revokedAt);

  const grantIt = async () => {
    if (!pickedAgent || pickedScopes.length === 0) return;
    try {
      await act("integration.grant", {
        integration: held.id,
        agent: pickedAgent,
        scopes: pickedScopes.map((scope) => ({ scope })),
      });
      setPicking(false);
      setPickedAgent(null);
      setPickedScopes([]);
      again();
    } catch {
      setSaying(say("integrations.grantFailed"));
    }
  };

  return (
    <Stack gap={5} testID="integration-details">
      {dialog}
      {held.error ? <Alert tone="danger">{held.error}</Alert> : null}

      <Card title={say("integrations.overview")}>
        <Facts
          items={[
            { label: say("integrations.section"), value: providerName(held.provider) },
            {
              label: say("integrations.status.connected"),
              value: <Status tone={statusTone(held.status)}>{statusWord(held.status)}</Status>,
            },
            {
              label: say("integrations.pickScopes"),
              value:
                live.length === 0 ? (
                  "—"
                ) : (
                  <Row gap={1} wrap>
                    {live.map((scope) => (
                      <Chip key={scope} size="sm">
                        {scopeWord(scope)}
                      </Chip>
                    ))}
                  </Row>
                ),
            },
          ]}
        />
      </Card>

      <Card
        title={say("integrations.access")}
        action={
          mine && !picking ? (
            <Button
              size="sm"
              variant="soft"
              icon="plus"
              testID="integration-grant-open"
              onPress={() => setPicking(true)}
            >
              {say("integrations.grant")}
            </Button>
          ) : null
        }
      >
        <Stack gap={2}>
          {grants === null ? <SkeletonRows rows={2} /> : null}
          {grants !== null && open.length === 0 && !picking ? (
            <Text typeName="body" color="text.tertiary">
              {say("integrations.grants.none")}
            </Text>
          ) : null}
          {open.map((one, index) => (
            <ListRow
              key={one.id}
              title={one.agent}
              value={one.scopes.map(scopeWord).join(", ")}
              face={{ name: one.agent }}
              last={index === open.length - 1}
              testID={`integration-grant-${one.agent}`}
              action={
                mine ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    testID={`integration-revoke-${one.agent}`}
                    onPress={() => {
                      void act("integration.revoke", { grant: one.id })
                        .then(again)
                        .catch(() => setSaying(say("integrations.grantFailed")));
                    }}
                  >
                    {say("integrations.revoke")}
                  </Button>
                ) : null
              }
            />
          ))}

          {mine && picking ? (
            <Stack gap={3} style={{ paddingTop: theme.space[2] }}>
              <Text typeName="caption" color="text.tertiary">
                {say("integrations.pickAgent")}
              </Text>
              <ChoiceChips
                size="sm"
                value={pickedAgent}
                onChange={setPickedAgent}
                options={agents.map((one) => ({
                  value: one.account,
                  label: one.handle,
                  icon: "bot",
                  testID: `integration-pick-${one.handle}`,
                }))}
              />
              <Text typeName="caption" color="text.tertiary">
                {say("integrations.pickScopes")}
              </Text>
              <ChoiceChips
                multiple
                size="sm"
                value={pickedScopes}
                onChange={setPickedScopes}
                options={live.map((scope) => ({
                  value: scope,
                  label: scopeWord(scope),
                  testID: `integration-scope-${scope}`,
                }))}
              />
              <Row gap={2}>
                <Button
                  size="sm"
                  disabled={!pickedAgent || pickedScopes.length === 0}
                  testID="integration-grant-press"
                  onPress={() => void grantIt()}
                >
                  {say("integrations.grant.press")}
                </Button>
                <Button size="sm" variant="ghost" onPress={() => setPicking(false)}>
                  {say("integrations.dismiss")}
                </Button>
              </Row>
            </Stack>
          ) : null}

          {saying ? <Alert tone="danger">{saying}</Alert> : null}
        </Stack>
      </Card>

      <Card title={say("integrations.activity")}>
        {events === null ? <SkeletonRows rows={3} /> : null}
        {events !== null && events.length === 0 ? (
          <Text typeName="body" color="text.tertiary">
            {say("integrations.activity.empty")}
          </Text>
        ) : null}
        <Stack gap={1}>
          {(events ?? []).map((one) => (
            <Row key={one.id} gap={2} align="center">
              <Badge tone="neutral" dot>
                {new Date(one.at).toLocaleDateString()}
              </Badge>
              <Text typeName="caption" color="text.secondary" style={{ flex: 1, minWidth: 0 }}>
                {eventLine(one)}
              </Text>
            </Row>
          ))}
        </Stack>
      </Card>

      <Card title={say("integrations.security")}>
        <Stack gap={3}>
          <Text typeName="body" color="text.secondary">
            {say("integrations.sealed")}
          </Text>
          {test ? <Alert tone={test.ok ? "success" : "danger"}>{test.said}</Alert> : null}
          {mine ? (
            <Row gap={2}>
              <Button
                size="sm"
                variant="outline"
                icon="zap"
                testID="integration-call-test"
                onPress={() => {
                  setTest(null);
                  void act("integration.call", {
                    integration: held.id,
                    act: "list",
                    scope: (held.scopes ?? [])[0],
                    limit: 5,
                  })
                    .then(() => setTest({ ok: true, said: say("integrations.test.ok") }))
                    .catch((cause: unknown) =>
                      setTest({
                        ok: false,
                        said: say("integrations.test.refused")(cause instanceof Error ? cause.message : ""),
                      }),
                    );
                }}
              >
                {say("integrations.callTest")}
              </Button>
            </Row>
          ) : null}
          {mine ? (
            <Stack gap={2}>
              <Text typeName="caption" color="text.tertiary">
                {say("integrations.disconnectWhy")}
              </Text>
              <Button
                size="sm"
                variant="danger"
                icon="unlock"
                testID="integration-disconnect"
                onPress={() => {
                  void ask({
                    title: say("integrations.disconnectTitle")(held.label),
                    description: say("integrations.disconnectWhy"),
                    confirmLabel: say("integrations.disconnect"),
                    cancelLabel: say("integrations.keep"),
                    tone: "danger",
                  }).then((sure) => {
                    if (!sure) return;
                    void act("integration.disconnect", { integration: held.id })
                      .then(again)
                      .catch(() => setSaying(say("integrations.disconnectFailed")));
                  });
                }}
              >
                {say("integrations.disconnect")}
              </Button>
            </Stack>
          ) : null}
        </Stack>
      </Card>
    </Stack>
  );
}
